import React, { useState } from "react";
import { ChevronDown, ChevronRight } from "react-feather";
import StatusBadge from "../../../../components/common/StatusBadge";
import { useListMaintenanceRequestsQuery, useGetMaintenanceRequestQuery } from "../vehiclesApi";
import VehicleRequestDetailCard from "./VehicleRequestDetailCard";

const money = (n) => Number(n || 0).toLocaleString("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const asDate = (v) => (v ? new Date(v).toLocaleDateString() : "-");

const PreviousRequestDetail = ({ recordGuid }) => {
  const { data, isError } = useGetMaintenanceRequestQuery(recordGuid);
  if (isError) return <p className="text-danger mb-0">Could not load this request.</p>;
  return <VehicleRequestDetailCard request={data} />;
};

/**
 * The vehicle's earlier maintenance requests, shown to the approver beside the
 * request being decided (2026-09-19) - "was this same job done last month?" is
 * the question an approval turns on, and answering it used to mean leaving for
 * the Vehicle Maintenance Report. Newest first; a row opens the full request
 * (work, spares, cash, who approved) in place.
 */
const VehiclePreviousRequests = ({ vehicleId, excludeRecordGuid }) => {
  const [openGuid, setOpenGuid] = useState(null);
  const { data, isLoading, isError } = useListMaintenanceRequestsQuery(
    { vehicleId, pageSize: 20 },
    { skip: !vehicleId }
  );

  const rows = (data?.items || []).filter((r) => r.recordGuid !== excludeRecordGuid);

  return (
    <div className="mb-3">
      <div className="fw-semibold mb-2" style={{ fontSize: 13 }}>
        Previous requests for this vehicle {rows.length > 0 && <span className="text-muted fw-normal">({rows.length})</span>}
      </div>
      {isLoading && <p className="text-muted mb-0" style={{ fontSize: 13 }}>Loading…</p>}
      {isError && <p className="text-danger mb-0" style={{ fontSize: 13 }}>Could not load the earlier requests.</p>}
      {!isLoading && !isError && rows.length === 0 && (
        <p className="text-muted mb-0" style={{ fontSize: 13 }}>This is the first request for this vehicle.</p>
      )}
      {rows.length > 0 && (
        <div className="border rounded" style={{ maxHeight: 320, overflowY: "auto", fontSize: 13 }}>
          {rows.map((r) => {
            const isOpen = openGuid === r.recordGuid;
            return (
              <div key={r.recordGuid} className="border-bottom">
                <button
                  type="button"
                  className="btn btn-link text-start text-reset text-decoration-none w-100 d-flex align-items-start gap-2 p-2"
                  onClick={() => setOpenGuid(isOpen ? null : r.recordGuid)}
                >
                  {isOpen ? <ChevronDown size={14} className="mt-1" /> : <ChevronRight size={14} className="mt-1" />}
                  <span className="flex-grow-1">
                    <span className="fw-semibold">{asDate(r.requestDate)}</span>
                    <span className="text-muted"> · {r.requestNumber}</span>
                    {r.currentMileage != null && <span className="text-muted"> · {r.currentMileage} km</span>}
                    <span className="d-block">{r.serviceRepairDescription || <span className="text-muted">no work description</span>}</span>
                    <span className="d-block text-muted">
                      {r.spareItemCount > 0 ? `${r.spareItemCount} spare item(s)` : "no spares"}
                      {r.approvedCashAmount != null
                        ? ` · cash approved ${money(r.approvedCashAmount)}`
                        : r.requestedCashAmount != null
                        ? ` · cash asked ${money(r.requestedCashAmount)}`
                        : ""}
                    </span>
                  </span>
                  <StatusBadge status={r.status} />
                </button>
                {isOpen && (
                  <div className="p-2 pt-0">
                    <PreviousRequestDetail recordGuid={r.recordGuid} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default VehiclePreviousRequests;
